import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiJson, setActiveOrganizationId } from '../services/api';


const ActorContext = createContext(null);

export function ActorProvider({ children }) {
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refreshActor = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const nextActor = await apiJson('/identity/context');
      setActor(nextActor);
      setActiveOrganizationId(nextActor.active_organization_id || null);
      return nextActor;
    } catch (requestError) {
      setError(requestError?.message || 'Could not load your workspace.');
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setActiveOrganizationId(null);
    refreshActor().catch(() => {});
    return () => setActiveOrganizationId(null);
  }, [refreshActor]);

  const selectOrganization = useCallback(async (organizationId) => {
    setActiveOrganizationId(organizationId);
    return refreshActor();
  }, [refreshActor]);

  const onboardOrganization = useCallback(async ({ name, slug }) => {
    const result = await apiJson('/identity/organizations/onboard', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, slug }),
    });
    setActiveOrganizationId(result.organization_id);
    await refreshActor();
    return result;
  }, [refreshActor]);

  const acceptInvitation = useCallback(async (token) => {
    const result = await apiJson('/identity/invitations/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    setActiveOrganizationId(result.organization_id);
    await refreshActor();
    return result;
  }, [refreshActor]);

  const value = useMemo(() => ({
    actor,
    loading,
    error,
    refreshActor,
    selectOrganization,
    onboardOrganization,
    acceptInvitation,
    hasPermission: (permissionKey) => Boolean(actor?.permission_keys?.includes(permissionKey)),
    hasRole: (roleKey) => Boolean(actor?.role_keys?.includes(roleKey)),
  }), [acceptInvitation, actor, error, loading, onboardOrganization, refreshActor, selectOrganization]);

  return <ActorContext.Provider value={value}>{children}</ActorContext.Provider>;
}

export function useActor() {
  const value = useContext(ActorContext);
  if (!value) throw new Error('useActor must be used inside ActorProvider.');
  return value;
}
